import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Tag,
  Card,
  Typography,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserService from '../services/user';
import type { UserListParams } from '../services/user';
import RoleService from '../services/role';
import { User } from '../services/auth';
import { Role } from '../services/role';
import useDocumentTitle from '../hooks/useDocumentTitle';
import userPreferenceService from '../services/userPreference';
import { getApiResponseErrorMessage, getAxiosErrorMessage } from '../utils/error';
import ListHelpButton from '../components/ListHelpButton';

const { Title, Text } = Typography;

const UserManagement: React.FC = () => {
  useDocumentTitle('用户管理 - 用户');
  const { hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>(''); // 角色筛选（空为全部）
  const [selectedParentId, setSelectedParentId] = useState<number | null | undefined>(undefined); // 上级用户筛选
  const [roles, setRoles] = useState<Role[]>([]);
  const [parentFilterOptions, setParentFilterOptions] = useState<Array<Pick<User, 'id' | 'username' | 'display_name' | 'email'>>>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState<string>(''); // 搜索输入框当前值
  const [searchKeyword, setSearchKeyword] = useState<string>(''); // 已提交的用户名/邮箱/客户名称搜索关键词
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({ current: 1, pageSize: 10, total: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pageSizeLoaded, setPageSizeLoaded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const current_page = pagination.current;
  const page_size = pagination.pageSize;

  // Check permissions
  const canView = hasPermission('user.view');
  const canCreate = hasPermission('user.create');
  const canUpdate = hasPermission('user.update');
  const canDelete = hasPermission('user.delete');

  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (!canView) {
      return;
    }

    if (isAdmin) {
      fetchRoles();
    }
  }, [canView, isAdmin]);

  // 加载每页条数设置（用户偏好）
  useEffect(() => {
    const loadPageSize = async () => {
      try {
        const saved = await userPreferenceService.getPageSizeSettings('users');
        if (saved && saved > 0) {
          setPagination(prev => {
            if (prev.pageSize === saved) {
              return prev;
            }

            return { ...prev, pageSize: saved };
          });
        }
      } catch (e) {
        // ignore
      } finally {
        setPageSizeLoaded(true);
      }
    };
    loadPageSize();
  }, []);

  // 搜索输入防抖，避免每次击键都立即请求后端
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchKeyword(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  // 筛选条件变化时回到第一页
  useEffect(() => {
    if (!pageSizeLoaded || !canView) {
      return;
    }

    setPagination(prev => {
      if (prev.current === 1) {
        return prev;
      }

      return { ...prev, current: 1 };
    });
  }, [searchKeyword, selectedRole, selectedParentId, pageSizeLoaded, canView]);

  // 分页或筛选条件变化时重新获取列表
  useEffect(() => {
    if (!pageSizeLoaded || !canView) {
      return;
    }

    const loadUsers = async () => {
      setLoading(true);
      try {
        const params: UserListParams = {
          page: current_page,
          per_page: page_size,
        };

        if (searchKeyword) {
          params.search = searchKeyword;
        }
        if (selectedRole) {
          params.role = selectedRole;
        }
        if (typeof selectedParentId !== 'undefined') {
          params.parent_id = selectedParentId === null ? 'null' : selectedParentId;
        }

        const response = await UserService.getUsersPage(params);
        if (response.success) {
          setUsers(response.data.data);
          setPagination(prev => ({
            ...prev,
            current: response.data.current_page,
            pageSize: response.data.per_page,
            total: response.data.total,
          }));
          setParentFilterOptions(response.filter_options?.parents || []);
        }
      } catch (error) {
        message.error('获取用户列表失败');
      } finally {
        setLoading(false);
      }
    };

    void loadUsers();
  }, [pageSizeLoaded, canView, current_page, page_size, searchKeyword, selectedRole, selectedParentId, refreshKey]);

  const fetchRoles = async () => {
    try {
      const response = await RoleService.getRoles();
      if (response.success && response.data) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
    }
  };

  const handleTableChange = async (pager: any) => {
    const isPageSizeChanged = pagination.pageSize !== pager.pageSize;
    if (isPageSizeChanged) {
      try { await userPreferenceService.savePageSizeSettings('users', pager.pageSize); } catch {}
    }
    setPagination(prev => ({ ...prev, current: pager.current, pageSize: pager.pageSize }));
  };

  const handleCreate = () => {
    navigate('/users/new');
  };

  const handleEdit = (user: User) => {
    // 导航到用户的个人设置页面，通过URL参数传递用户ID
    navigate(`/profile?userId=${user.id}`);
  };

  const handleDelete = async (userId: number) => {
    try {
      const response = await UserService.deleteUser(userId);
      if (response.success) {
        message.success('用户删除成功');
        setRefreshKey(prev => prev + 1);
      } else {
        message.error(getApiResponseErrorMessage(response, '删除失败'));
      }
    } catch (error: any) {
      message.error(getAxiosErrorMessage(error, '删除失败'));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: Omit<UserListParams, 'page' | 'per_page'> = {};

      if (searchKeyword) {
        params.search = searchKeyword;
      }
      if (selectedRole) {
        params.role = selectedRole;
      }
      if (typeof selectedParentId !== 'undefined') {
        params.parent_id = selectedParentId === null ? 'null' : selectedParentId;
      }

      const blob = await UserService.exportUsers(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `用户数据_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 20,
    },
    {
      title: '登录名',
      dataIndex: 'username',
      key: 'username',
      width: 140,
    },
    {
      title: '客户名称',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 140,
      render: (value: string) => value || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 120,
    },
    {
      title: selectedRole === 'client' || selectedRole === 'peers' ? '所属销售' : '上级',
      dataIndex: 'parent',
      key: 'parent',
      width: 160,
      render: (_: any, record: any) => {
        const parent = record.parent || users.find((u: any) => u.id === record.parent_id);
        if (!parent) return '-';
        return parent.display_name || parent.username || parent.email || `ID:${parent.id}`;
      }
    },
    ...(selectedRole === 'client' || selectedRole === 'peers' ? [
      {
        title: '客户等级',
        dataIndex: 'client_type',
        key: 'client_type',
        width: 120,
        render: (_: any, record: any) => record.client_type || '-',
      },
      {
        title: '客户群号',
        dataIndex: 'group_number',
        key: 'group_number',
        width: 140,
        render: (_: any, record: any) => record.group_number || '-',
      },
      {
        title: '客户来源',
        dataIndex: 'source',
        key: 'source',
        width: 180,
        render: (_: any, record: any) => record.source || '-',
      },
    ] : []),
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      width: 80,
      render: (roles: Role[]) => (
        <>
          {roles.map(role => (
            <Tag key={role.id} color="blue">
              {role.display_name || role.name}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: User) => (
        <Space size="middle">
          {canUpdate && (
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="确定要删除这个用户吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!canView) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={4}>没有权限访问用户管理</Title>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <Space size="small">
            <span>用户管理</span>
            <ListHelpButton page_key="user_management" />
          </Space>
        )}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
              导出数据
            </Button>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                添加用户
              </Button>
            )}
            {canDelete && selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 个用户吗？`}
                okText="确定"
                cancelText="取消"
                onConfirm={async () => {
                  try {
                    const ids = selectedRowKeys as number[];
                    const response = await UserService.batchDeleteUsers(ids);
                    if (response.success) {
                      message.success(response.message || `成功删除 ${response.data?.deleted_count || ids.length} 个用户`);
                      setSelectedRowKeys([]);
                      setRefreshKey(prev => prev + 1);
                    } else {
                      message.error(response.message || '批量删除失败');
                    }
                  } catch (error: any) {
                    message.error(error?.response?.data?.message || '批量删除失败');
                  }
                }}
                disabled={!selectedRowKeys.length}
              >
                <Button danger disabled={!selectedRowKeys.length}>批量删除</Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
      {isAdmin && (
        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space wrap size={[8, 8]}>
            <Text strong>按角色筛选：</Text>
            <Tag
              color={!selectedRole ? 'blue' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedRole('')}
            >全部</Tag>
            {roles.map(role => (
              <Tag
                key={role.id}
                color={selectedRole === role.name ? 'blue' : undefined}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedRole(role.name)}
              >
                {role.display_name || role.name}
              </Tag>
            ))}
          </Space>

          {/* 新建按钮移至卡片 head */}
        </div>
      )}

      {isAdmin && (
        // 按上级用户筛选（仅管理员可见）
        <div style={{ marginBottom: 8 }}>
          <Space wrap>
            <Text strong>按上级筛选：</Text>
            <Tag
              color={typeof selectedParentId === 'undefined' ? 'blue' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedParentId(undefined)}
            >全部</Tag>
            <Tag
              color={selectedParentId === null ? 'blue' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedParentId(null)}
            >无上级</Tag>
            {parentFilterOptions.map((p: any) => (
              <Tag
                key={p.id}
                color={selectedParentId === p.id ? 'blue' : undefined}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedParentId(p.id)}
              >{(p.display_name || p.username)}</Tag>
            ))}
          </Space>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="搜索登录名、邮箱或客户名称"
          style={{ width: 320 }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={(v) => setSearchInput(v)}
        />
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        rowSelection={canDelete ? ({ selectedRowKeys, onChange: setSelectedRowKeys as any, preserveSelectedRowKeys: true } as any) : undefined}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />
    </Card>
    </div>
  );
};

export default UserManagement;
